/**
 * Contested-law decisions — v11.7 V117-1-10 (ADR-0111 §7, D-116-14). The "Decision needed" view's API client and
 * its pure model (labels, grouping by law, sort order, Today wording). No React here.
 *
 *   GET  /api/w/:slug/rules/applicability-decisions — any member; the workspace's contested rules, deciders, can_decide
 *   POST /api/w/:slug/rules/:ruleId/applicability    — owner / admin; { choice, reason, expected_chosen_at }
 *
 * The API is per rule; a law (one `legislation_id`) can hold several rules (Colorado has four). The view shows one card
 * per law and saves the same choice and reason to each of its rules, each with the `expected_chosen_at` it last read.
 */
import { req } from './api';

export type ApplicabilityChoice = 'applies' | 'does_not_apply';

export interface DecisionRecordApi {
  choice: ApplicabilityChoice;
  reason: string;
  chosen_by: string;
  chosen_by_name?: string | null;
  chosen_at: string;
}

export interface RuleDecisionApi {
  rule_id: string;
  jurisdiction: string;
  statute: string;
  citation: string;
  legislation_id: string;
  law_title: string | null;
  legal_status: string | null;
  stay_kind: string | null;
  preemption_risk: string | null;
  preemption_note: string | null;
  effective_date: string | null;
  state: 'decision_needed' | ApplicabilityChoice;
  choice: (DecisionRecordApi & { workspace_id?: string; rule_id?: string }) | null;
  system_ids?: string[];
  systems: Array<{ system_id: string; name: string }>;
  history: DecisionRecordApi[];
}

export interface RuleDecider { user_id: string; name: string; email: string; role: 'owner' | 'admin' }

export interface RuleDecisionsView {
  decisions: RuleDecisionApi[];
  decision_needed: number;
  deciders: RuleDecider[];
  can_decide: boolean;
}

export const decisionKeys = { all: (slug: string) => ['rule-decisions', slug] as const };

export const getRuleDecisions = (slug: string) =>
  req<RuleDecisionsView>(`/api/w/${encodeURIComponent(slug)}/rules/applicability-decisions`);

export const chooseRuleApplicability = (slug: string, ruleId: string, body: { choice: ApplicabilityChoice; reason: string; expected_chosen_at: string | null }) =>
  req<{ choice: DecisionRecordApi }>(`/api/w/${encodeURIComponent(slug)}/rules/${encodeURIComponent(ruleId)}/applicability`, { method: 'POST', body: JSON.stringify(body) });

// ---- Model -----------------------------------------------------------------------------------------------------

export interface DecisionRecord { choice: ApplicabilityChoice; reason: string; by: string; byName: string; at: string }

export interface LawDecision {
  /** The law (legislation_id); one card per law. */
  key: string;
  name: string;
  rules: RuleDecisionApi[];
  stayLabel: string;
  /** 'pause' for a stay, 'risk' for preemption risk alone (the glyph). */
  stayIcon: 'pause' | 'risk';
  plain: string;
  /** The citation text (behind "Source"), never in the primary text. */
  note: string | null;
  effective: string | null;
  systems: Array<{ system_id: string; name: string }>;
  /** Null while any of the law's rules waits on a decision. */
  current: DecisionRecord | null;
  history: DecisionRecord[];
}

export const CHOICE_WORD: Record<ApplicabilityChoice, string> = { applies: 'Applies', does_not_apply: 'Does not apply' };

/** The label from what the law records: the stay kind first, then high preemption risk. */
export function stayLabel(d: Pick<RuleDecisionApi, 'legal_status' | 'stay_kind' | 'preemption_risk'>): string {
  if (d.legal_status === 'stayed') return d.stay_kind === 'standstill' ? 'Enforcement paused' : d.stay_kind === 'court_order' ? 'Stayed by court' : 'Stayed';
  if (d.preemption_risk === 'high') return 'At risk of preemption';
  return 'Stayed';
}

/** One short, plain sentence: what the label means. States facts only; never advises. */
export function plainLine(d: Pick<RuleDecisionApi, 'legal_status' | 'stay_kind' | 'preemption_risk'>): string {
  if (d.legal_status === 'stayed') {
    if (d.stay_kind === 'standstill') return 'The state has agreed not to enforce this law while a lawsuit against it is decided.';
    if (d.stay_kind === 'court_order') return 'A court has ordered that this law not be enforced while a lawsuit against it is decided.';
    return 'Enforcement of this law is on hold while a lawsuit against it is decided.';
  }
  return 'A federal rule may override this state law. Nothing is paused yet.';
}

const recordOf = (r: DecisionRecordApi): DecisionRecord => ({ choice: r.choice, reason: r.reason, by: r.chosen_by, byName: r.chosen_by_name || 'Someone', at: r.chosen_at });

/** One card per law, its rules' decisions merged (a save writes every rule, a few ms apart). */
export function groupByLaw(decisions: readonly RuleDecisionApi[]): LawDecision[] {
  const byLaw = new Map<string, RuleDecisionApi[]>();
  for (const d of decisions) byLaw.set(d.legislation_id, [...(byLaw.get(d.legislation_id) ?? []), d]);
  return [...byLaw.entries()].map(([key, rules]) => {
    const first = rules[0]!;
    const systems = new Map<string, { system_id: string; name: string }>();
    for (const r of rules) for (const s of r.systems ?? []) systems.set(s.system_id, s);
    const effective = rules.map((r) => r.effective_date).filter((x): x is string => !!x).sort()[0] ?? null;
    const decided = rules.every((r) => r.choice);
    const latest = rules.map((r) => r.choice).filter((c): c is NonNullable<typeof c> => !!c).sort((a, b) => b.chosen_at.localeCompare(a.chosen_at))[0];
    const seen = new Set<string>();
    const history = rules.flatMap((r) => r.history ?? []).map(recordOf)
      .sort((a, b) => b.at.localeCompare(a.at))
      .filter((h) => {
        const k = `${h.choice}|${h.by}|${h.reason}|${h.at.slice(0, 16)}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    return {
      key, rules, name: first.law_title || first.statute,
      stayLabel: stayLabel(first), stayIcon: first.legal_status === 'stayed' ? 'pause' : 'risk',
      plain: plainLine(first), note: first.preemption_note, effective,
      systems: [...systems.values()].sort((a, b) => a.name.localeCompare(b.name)),
      current: decided && latest ? recordOf(latest) : null,
      history,
    };
  });
}

/** Waiting laws by effective date, earliest first (no date last); decided laws by decision date, newest first. */
export function splitLaws(laws: readonly LawDecision[]): { waiting: LawDecision[]; decided: LawDecision[] } {
  const waiting = laws.filter((l) => !l.current).sort((a, b) => (a.effective ?? '9999').localeCompare(b.effective ?? '9999') || a.name.localeCompare(b.name));
  const decided = laws.filter((l) => l.current).sort((a, b) => b.current!.at.localeCompare(a.current!.at));
  return { waiting, decided };
}

/** The law card a `&rule=` deep link names (any of the law's rules). */
export const lawOfRule = (laws: readonly LawDecision[], ruleId: string | null) => (ruleId ? laws.find((l) => l.rules.some((r) => r.rule_id === ruleId)) ?? null : null);

const DAY = 86_400_000;
const dayOf = (iso: string) => Date.parse(`${iso.slice(0, 10)}T12:00:00Z`);
export const isFuture = (iso: string, today: string) => dayOf(iso) > dayOf(today);
export const daysUntil = (iso: string, today: string) => Math.round((dayOf(iso) - dayOf(today)) / DAY);

/** "Jan 1, 2027" (UTC; dates are calendar days). */
export function fmtLawDate(iso: string): string {
  return new Date(dayOf(iso)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** "Takes effect" for a future date, "Took effect" for a past one. */
export const effWord = (iso: string, today: string) => (isFuture(iso, today) ? 'Takes effect' : 'Took effect');

/** Today item / Scope entry wording. One law: its name and date. Several: the count, then the urgency. */
export function todayTitle(waiting: readonly LawDecision[], today: string): string {
  const n = waiting.length;
  if (n === 0) return '';
  if (n === 1) {
    const l = waiting[0]!;
    return l.effective ? `${l.name} needs your decision · ${effWord(l.effective, today).toLowerCase()} ${fmtLawDate(l.effective)}` : `${l.name} needs your decision`;
  }
  const inEffect = waiting.filter((l) => l.effective && !isFuture(l.effective, today)).length;
  if (inEffect) return `${n} laws need your decision · ${inEffect === 1 ? 'one' : String(inEffect)} already in effect`;
  const next = waiting.find((l) => l.effective);
  return next?.effective ? `${n} laws need your decision · next takes effect ${fmtLawDate(next.effective)}` : `${n} laws need your decision`;
}

export function todaySub(waiting: readonly LawDecision[]): string {
  if (waiting.length === 1) return `${waiting[0]!.stayLabel} · Not counted until decided`;
  const named = waiting.slice(0, 3).map((l) => (l.effective ? `${l.name} (${fmtLawDate(l.effective)})` : l.name)).join(', ');
  return `${named}${waiting.length > 3 ? ` and ${waiting.length - 3} more` : ''} · Not counted until decided`;
}

/** The AI Regulatory Scope › Decisions route (optionally one law, and where the reader came from). */
export function decisionsHref(slug: string, ruleId?: string | null, from?: string | null): string {
  const q = new URLSearchParams({ view: 'decisions' });
  if (ruleId) q.set('rule', ruleId);
  if (from) q.set('from', from);
  return `/w/${encodeURIComponent(slug)}/app/programs/ai-regulatory/scope?${q.toString()}`;
}

/** "Review Colorado SB 26-189" for one, "Review 2 decisions" for several; the target is the first waiting law. */
export function reviewTarget(slug: string, waiting: readonly LawDecision[]): { label: string; href: string } {
  return waiting.length === 1
    ? { label: `Review ${waiting[0]!.name}`, href: decisionsHref(slug, waiting[0]!.rules[0]!.rule_id) }
    : { label: `Review ${waiting.length} decisions`, href: decisionsHref(slug) };
}

/** A `from=` return path is honoured only inside this workspace's app (no open redirect). */
export function safeFrom(slug: string, from: string | null): string | null {
  if (!from) return null;
  const prefix = `/w/${encodeURIComponent(slug)}/app/`;
  return from.startsWith(prefix) && !from.includes('//') && !/[\s\\]/.test(from) ? from : null;
}

/** "4 minutes ago" under an hour, else "on Oct 29, 2026". */
export function whenText(iso: string, now = Date.now()): string {
  const mins = Math.round((now - Date.parse(iso)) / 60_000);
  if (mins >= 0 && mins < 60) return mins <= 1 ? 'a minute ago' : `${mins} minutes ago`;
  return `on ${fmtLawDate(iso)}`;
}

export const firstName = (name: string) => name.split(/\s+/)[0] || name;
